import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BadgeChip } from "@/components/BadgeChip";
import { Search, Gauge } from "lucide-react";

function EditRow({ p, onDone }) {
  const [exact, setExact] = useState("");
  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = async (payload) => {
    if (!reason.trim()) return toast.error("Add a short reason first");
    setBusy(true);
    try {
      const r = await api.post("/admin/psi/set", { user_id: p.id, reason, override_goat: override, ...payload });
      toast.success(`${p.name}: PSI ${r.data.old} → ${r.data.new} (${r.data.division})`);
      setExact(""); setReason(""); setOverride(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-black/40 border border-pb-border p-3 mt-2 space-y-2" data-testid={`psi-edit-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid={`psi-exact-${p.id}`} type="number" min="0" max="198" step="0.1" value={exact} onChange={(e) => setExact(e.target.value)}
          placeholder={`${p.rating}`} className="w-24 bg-black border border-pb-border text-center font-mono font-black text-white px-1 py-1.5 focus:border-pb-cyan outline-none text-sm" />
        <button data-testid={`psi-set-${p.id}`} disabled={busy || exact === ""} onClick={() => apply({ psi: Number(exact) })}
          className="bg-pb-red text-black text-[9px] font-black uppercase tracking-widest px-3 py-2 disabled:opacity-40">Set Exact</button>
        <span className="text-zinc-700 text-xs">|</span>
        {[-5, -1, -0.5, 0.5, 1, 5].map((d) => (
          <button key={d} data-testid={`psi-nudge-${d}-${p.id}`} disabled={busy} onClick={() => apply({ delta: d })}
            className={`border text-[10px] font-black font-mono px-2 py-1.5 disabled:opacity-40 ${d > 0 ? "border-pb-red/50 text-pb-red hover:bg-pb-red/10" : "border-red-500/50 text-red-400 hover:bg-red-500/10"}`}>
            {d > 0 ? `+${d}` : d}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid={`psi-reason-${p.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required) — e.g. score correction, sandbagging review"
          className="flex-1 min-w-[220px] bg-black border border-pb-border text-white text-xs px-2 py-2 focus:border-pb-cyan outline-none" />
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 cursor-pointer">
          <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid={`psi-goat-override-${p.id}`} />
          override G.O.A.T. law
        </label>
      </div>
    </div>
  );
}

export const PsiControl = () => {
  const [q, setQ] = useState("");
  const [players, setPlayers] = useState([]);
  const [log, setLog] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/psi/players", { params: { q } }),
      api.get("/admin/psi/log"),
    ]);
    setPlayers(a.data.players);
    setLog(b.data.log);
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="psi-control">
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-4 h-4 text-zinc-600" />
        <input data-testid="psi-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search player by name or email…"
          className="flex-1 bg-black border border-pb-border text-white text-sm px-3 py-2 focus:border-pb-cyan outline-none" />
      </div>
      <div className="divide-y divide-pb-border/50 max-h-[420px] overflow-y-auto">
        {players.map((p) => (
          <div key={p.id} className="py-2">
            <button onClick={() => setOpen(open === p.id ? null : p.id)} className="w-full text-left flex flex-wrap items-center gap-x-3 gap-y-1 hover:bg-white/5 px-1 py-1"
              data-testid={`psi-row-${p.id}`}>
              <span className="font-bold text-sm text-white">{p.name}</span>
              <span className="text-[10px] text-zinc-600">{p.email}</span>
              <span className="ml-auto font-mono font-black text-pb-cyan text-sm" data-testid={`psi-val-${p.id}`}>{p.psi}</span>
              <BadgeChip badge={p.badge} />
              <span className="text-[9px] uppercase tracking-widest text-zinc-500">{p.division}</span>
              <span className="text-[9px] font-mono text-zinc-600">anchor {p.baseline}</span>
              <span className="text-[9px] font-mono text-zinc-600">{p.w}W-{p.l}L</span>
              {p.last_moved && <span className="text-[9px] text-zinc-700">moved {String(p.last_moved).slice(0, 10)}</span>}
            </button>
            {open === p.id && <EditRow p={p} onDone={load} />}
          </div>
        ))}
        {players.length === 0 && <div className="text-zinc-600 text-xs py-4 text-center">No players match.</div>}
      </div>

      <div className="mt-4 border-t border-pb-border pt-3">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-2">
          <Gauge className="w-3.5 h-3.5" /> Recent PSI Adjustments
        </div>
        <div className="space-y-1 max-h-[180px] overflow-y-auto" data-testid="psi-adjustment-log">
          {log.map((r) => (
            <div key={r.id} className="text-xs flex flex-wrap gap-x-2 text-zinc-400">
              <span className="text-zinc-600">{String(r.at).slice(0, 16).replace("T", " ")}</span>
              <span className="font-bold text-white">{r.user_name}</span>
              <span className="font-mono">{r.old} → <span className="text-pb-cyan font-black">{r.new}</span></span>
              <span className="text-zinc-500 italic">“{r.reason}”</span>
              <span className="text-zinc-700">by {r.admin_name}</span>
              {r.override_goat && <span className="text-amber-400 text-[9px] uppercase font-black">goat override</span>}
            </div>
          ))}
          {log.length === 0 && <div className="text-zinc-600 text-xs">No manual adjustments yet.</div>}
        </div>
      </div>
    </div>
  );
};
